import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getApiBase } from '../services/api';

/* Colori delle zone, come nella mappa clienti */
const COLORI_ZONA = {
  'Appia':'#dc2626','Aurelio':'#ea580c','Casalotti-Palmarola':'#d97706',
  'Casilina-Tuscolana':'#ca8a04','Cassia-Flaminia':'#65a30d','Centro Storico':'#16a34a',
  'EUR-Magliana':'#059669','Fuori Roma':'#0d9488','Marconi':'#0891b2',
  'Nomentana-Salaria':'#0284c7','Ostiense':'#2563eb','Portuense-Bravetta':'#4f46e5',
  'Prati-Vaticano-Parioli':'#7c3aed','Tiburtina-Prenestina':'#c026d3','Torrevecchia-Boccea':'#db2777',
};
const coloreZona = z => COLORI_ZONA[z] || '#94a3b8';

const GIORNI = ['Dom','Lun','Mar','Mer','Gio','Ven','Sab'];

function useLeaflet() {
  const [pronto, setPronto] = useState(!!window.L);
  useEffect(() => {
    if (window.L) { setPronto(true); return; }
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css';
    document.head.appendChild(css);
    const js = document.createElement('script');
    js.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js';
    js.onload = () => setPronto(true);
    document.body.appendChild(js);
  }, []);
  return pronto;
}

function Riquadro({ valore, etichetta, colore, grande }) {
  return (
    <div style={{
      flex:1, background:'#132038', borderRadius:12, padding: grande ? '18px 20px' : '14px 18px',
      borderLeft:`5px solid ${colore}`, minWidth:0,
    }}>
      <div style={{ fontSize: grande ? 46 : 34, fontWeight:800, color:'#fff', lineHeight:1 }}>
        {valore ?? '—'}
      </div>
      <div style={{ fontSize: grande ? 14 : 12, color:'#8fa3bf', marginTop:6,
                    textTransform:'uppercase', letterSpacing:1 }}>
        {etichetta}
      </div>
    </div>
  );
}

export default function TvDashboard() {
  const { token } = useParams();
  const leafletPronto = useLeaflet();

  const [dati, setDati]   = useState(null);
  const [errore, setErr]  = useState(false);
  const [ora, setOra]     = useState(new Date());

  const divRef = useRef(null);
  const mappaRef = useRef(null);
  const livelliRef = useRef([]);

  /* Orologio */
  useEffect(() => {
    const t = setInterval(() => setOra(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  /* Dati, con aggiornamento automatico */
  const carica = useCallback(() => {
    fetch(`${getApiBase()}/api/tv/${token}/dati`)
      .then(r => { if (!r.ok) throw new Error('accesso non valido'); return r.json(); })
      .then(d => { setDati(d); setErr(false); })
      .catch(() => setErr(true));
  }, [token]);

  useEffect(() => {
    carica();
    const t = setInterval(carica, 60000);
    return () => clearInterval(t);
  }, [carica]);

  /* Mappa */
  useEffect(() => {
    if (!leafletPronto || mappaRef.current || !divRef.current) return;
    const L = window.L;
    const m = L.map(divRef.current, { zoomControl:false, attributionControl:false })
               .setView([41.9028, 12.4964], 11);
    // CARTO ora richiede una API key: si usa Esri Dark Gray, che non ne ha bisogno
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { maxZoom: 16, attribution: 'Esri' }).addTo(m);

    // Sede SOS FIRE
    const iconaSede = L.icon({
      iconUrl: '/sosfire-marker.png',
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
    L.marker([41.857694, 12.537111], { icon: iconaSede, zIndexOffset: -500 })
      .addTo(m)
      .bindTooltip('Sede', { direction: 'top', offset: [0, -14] });
    mappaRef.current = m;
  }, [leafletPronto]);

  /* Profilo di furgone: il colore segnala lo stato del mezzo,
     l'alone lo stacca dallo sfondo scuro della mappa. */
  const furgone = (col) => `
    <svg viewBox="0 0 76 40" width="23" height="15" preserveAspectRatio="none"
         style="filter:drop-shadow(0 0 6px ${col})">
      <path d="M4 8h34v18H4z M38 8h16l12 10v8H38z"
            fill="#b30000" stroke="#1f2937" stroke-width="2"
            stroke-linejoin="round"/>
      <path d="M41 11h11l8 7H41z" fill="#e5e7eb"/>
      <rect x="4" y="26" width="62" height="4" rx="1" fill="#1f2937"/>
      <circle cx="18" cy="30" r="6" fill="#1f2937"/>
      <circle cx="18" cy="30" r="2.5" fill="#9ca3af"/>
      <circle cx="56" cy="30" r="6" fill="#1f2937"/>
      <circle cx="56" cy="30" r="2.5" fill="#9ca3af"/>
      <circle cx="70" cy="16" r="3" fill="${col}"/>
    </svg>`;

  /* Puntini clienti + mezzi */
  useEffect(() => {
    const m = mappaRef.current;
    if (!m || !window.L || !dati) return;
    const L = window.L;

    livelliRef.current.forEach(l => m.removeLayer(l));
    livelliRef.current = [];

    (dati.clienti || []).forEach(c => {
      const cerchio = L.circleMarker([c.lat, c.lng], {
        radius: 3.5, color: coloreZona(c.zona), fillColor: coloreZona(c.zona),
        fillOpacity: 0.8, weight: 0,
      }).addTo(m);
      livelliRef.current.push(cerchio);
    });

    (dati.interventiOggi || []).forEach(i => {
      const col = i.stato === 'completato' ? '#22c55e'
                : i.stato === 'in_corso'   ? '#f59e0b' : '#38bdf8';
      const anello = L.circleMarker([i.lat, i.lng], {
        radius: 11, color: col, fillColor: col, fillOpacity: 0.18, weight: 2.5,
      }).addTo(m);
      livelliRef.current.push(anello);
    });

    (dati.veicoli || []).forEach(v => {
      const col = !v.recente ? '#64748b' : (v.inMovimento ? '#f97316' : '#fbbf24');
      const icona = L.divIcon({
        className: '',
        html: furgone(col),
        iconSize: [23,15], iconAnchor: [12,8],
      });
      const mk = L.marker([v.lat, v.lng], { icon: icona }).addTo(m);
      livelliRef.current.push(mk);
    });
  }, [dati]);

  const s = dati?.stat || {};
  const impianti = (s.estintori || 0) + (s.idranti || 0) + (s.porte || 0);

  if (errore) {
    return (
      <div style={{ height:'100vh', background:'#0b1220', color:'#8fa3bf',
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
        Indirizzo non valido
      </div>
    );
  }

  return (
    <div style={{ height:'100vh', width:'100vw', background:'#0b1220',
                  display:'flex', flexDirection:'column', padding:18, gap:14,
                  fontFamily:'Segoe UI, system-ui, sans-serif', overflow:'hidden', boxSizing:'border-box' }}>

      {/* Intestazione */}
      <div style={{ display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:26, fontWeight:800, color:'#fff', letterSpacing:1 }}>
          <span style={{ color:'#dc2626' }}>{dati?.azienda || 'SOS FIRE'}</span><span style={{ color:'#8fa3bf', fontWeight:400, fontSize:20 }}> — Situazione operativa</span>
        </div>
        <div style={{ marginLeft:'auto', textAlign:'right' }}>
          <div style={{ fontSize:34, fontWeight:800, color:'#fff', lineHeight:1 }}>
            {ora.toLocaleTimeString('it-IT', { hour:'2-digit', minute:'2-digit' })}
          </div>
          <div style={{ fontSize:13, color:'#8fa3bf' }}>
            {ora.toLocaleDateString('it-IT', { weekday:'long', day:'numeric', month:'long' })}
            {dati?.aggiornato ? ` · dati delle ${dati.aggiornato}` : ''}
          </div>
        </div>
      </div>

      {/* Riquadri principali */}
      <div style={{ display:'flex', gap:12 }}>
        <Riquadro grande valore={s.programmati}      etichetta="Interventi programmati" colore="#38bdf8" />
        <Riquadro grande valore={s.settimana}        etichetta="Nei prossimi 7 giorni"  colore="#818cf8" />
        <Riquadro grande valore={s.in_corso}         etichetta="In corso adesso"        colore="#f59e0b" />
        <Riquadro grande valore={s.completati_mese}  etichetta="Completati nel mese"    colore="#22c55e" />
        <Riquadro grande valore={(dati?.veicoli || []).filter(v => v.recente).length}
                  etichetta="Mezzi collegati" colore="#f97316" />
      </div>

      {/* Mappa e colonna laterale */}
      <div style={{ flex:1, display:'flex', gap:14, minHeight:0 }}>
        <div ref={divRef} style={{ flex:3, borderRadius:14, overflow:'hidden', background:'#132038' }} />

        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:12, minWidth:230 }}>
          <div style={{ display:'flex', gap:12 }}>
            <Riquadro valore={s.clienti}   etichetta="Clienti attivi" colore="#0ea5e9" />
            <Riquadro valore={s.contratti} etichetta="Contratti"      colore="#8b5cf6" />
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <Riquadro valore={impianti.toLocaleString('it-IT')} etichetta="Impianti" colore="#14b8a6" />
            <Riquadro valore={s.contratti_scadenza} etichetta="Contratti in scadenza" colore="#f43f5e" />
          </div>

          {/* Agenda dei prossimi giorni */}
          <div style={{ flex:1, background:'#132038', borderRadius:12, padding:'14px 16px',
                        display:'flex', flexDirection:'column', minHeight:0 }}>
            <div style={{ fontSize:12, color:'#8fa3bf', textTransform:'uppercase',
                          letterSpacing:1, marginBottom:10 }}>
              Prossimi interventi
            </div>
            <div style={{ flex:1, overflow:'hidden' }}>
              {(dati?.agenda || []).slice(0, 9).map((g, i) => {
                const d = new Date(g.giorno);
                const oggi = new Date().toDateString() === d.toDateString();
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
                                        padding:'6px 0', borderBottom:'1px solid #1e2d4a' }}>
                    <div style={{ width:64, fontSize:13, color: oggi ? '#fff' : '#8fa3bf',
                                  fontWeight: oggi ? 700 : 400 }}>
                      {GIORNI[d.getDay()]} {d.getDate()}/{d.getMonth()+1}
                    </div>
                    <div style={{ flex:1, height:8, background:'#1e2d4a', borderRadius:4, overflow:'hidden' }}>
                      <div style={{ width:`${Math.min(100, g.quanti * 12)}%`, height:'100%',
                                    background: oggi ? '#38bdf8' : '#3b6ea5' }} />
                    </div>
                    <div style={{ fontSize:16, fontWeight:700, color:'#fff', width:26, textAlign:'right' }}>
                      {g.quanti}
                    </div>
                  </div>
                );
              })}
              {(dati?.agenda || []).length === 0 && (
                <div style={{ color:'#64748b', fontSize:13 }}>Nessun intervento in programma</div>
              )}
            </div>
          </div>

          {s.scia_scadenza > 0 && (
            <div style={{ background:'#3b1219', borderRadius:12, padding:'12px 16px',
                          borderLeft:'5px solid #f43f5e' }}>
              <div style={{ fontSize:26, fontWeight:800, color:'#fff' }}>{s.scia_scadenza}</div>
              <div style={{ fontSize:12, color:'#fca5a5', textTransform:'uppercase', letterSpacing:1 }}>
                SCIA in scadenza
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
